/* eslint-disable react/jsx-no-target-blank */
import { useMemo } from 'react';

import Link from 'next/link';

import Typography from '@components/Typography';
import MyFavoriteComponent from '@components/views/my-favorites/MyFavoriteComponent';
import { SocialShare } from '@components/ui/social-share';
import {
  FavoriteItemType,
  HotelPromotionType,
  RatingProviderType,
} from '@api/v3';
import ListWorldLogo from '@components/ui/ListWorldLogo';
import CustomIcon from '@components/utils/CustomIcon';
import {
  AirportTransferNext,
  ChevronRight,
  InfoIcon,
  SkipassIconNext,
  SmilingStar,
  SustainabilityIconNext,
} from '@assets/icons';
import { getCurrencySymbol, getNightCount } from '@utils/utils';
import { getDiscountType } from '@components/views/hotel/utils';
import HotelListingEvents from '@components/views/hotel/listing/components/Events';
import BankPosCard from '@components/ui/BankPosCard';

import { ListingItemProps } from '../ListingItemInterface';
import ListingItemSlider from './ListingItemSlider';
import {
  ShowHotelButtonWithDateSelection,
  ListItemStarContainer,
  MobileBoardTypeContainer,
  MobileDateSelectionButton,
  MobileDateSelectionButtonContainer,
  MobileDateSelectionContainer,
  MobileDateSelectionWarningMessage,
  MobileHotelNameTextWrapper,
  MobileLocationContainer,
  MobileNameContainer,
  MobileNameWrapper,
  MobilePriceAndBadgeWrapper,
  MobileStyledListingImageWrapper,
  MoileStyledListingItem,
  PaymentTypesMobileBadgeWrapper,
  ShareWrapperMobile,
  MobileDiscountAndNightCountWrapper,
  MobilePricingContainer,
  NightDiscountWrapperWithDetails,
  NightDiscountWrapperWithoutDetails,
  MobileSelectHotelButton,
  MobileBadgeContainer,
  PeriodTypeWrapper,
  MoileStyledListingItemWrapper,
  EventsRowDesktop,
  EventsBadgeWrapper,
  DistanceWrapper,
} from './styled';
import BoardType from './BoardType';
import ScoringBanner from './ScoringListing';
import NightsCount from './night-count';
import PriceLabel from './price/PriceLabel';
import DiscountLabel from './discount/DiscountLabel';
import Badge from './Badge';
import Features from './Features';
import Promotions from './Promotions';
import { periodTypeViewver } from './utils';
import Difference from './difference';
import ExpediaScoringBanner from './ExpediaListingScoring';

const MobileListingItem = ({
  fallBackImage,
  name,
  pricing: price,
  highlights,
  location,
  boardType,
  boardTypes,
  checkInDate,
  checkOutDate,
  expedia,
  url,
  secure,
  suggests,
  installments,
  onGoToHotelDetailClick,
  seturExecutive,
  hotelListing,
  badge = [],
  cyprus,
  link,
  getStars,
  itemPromotions,
  originalPrice,
  handleDateSelection,
  onGoToHotelDetail,
  distanceList,
  id,
  favoriteLists,
  refetchFavoriteLists,
  defaultList,
  defaultListItems,
  refetchDefaultList,
  itemImages,
  periodType,
  initialItem,
  events,
  diffData,
  differenceItem,
  addDiffData,
  removeDiffData,
  setDiffData,
  countryCode,
  hasEvents,
  ratings,
  country,
  setIsRedirecting,
  basketHotelCount,
}: ListingItemProps) => {
  const hasDiscount = price?.rate ? true : false;

  const showBadgeContainer = useMemo(() => {
    return (
      badge.some(
        (item) => item.value === 'Misafirler Öneriyor' && item.badge
      ) ||
      badge.some(
        (item) =>
          item.value === 'Sürdürülebilir Turizm Sertifikası' && item.badge
      ) ||
      badge.some((item) => item.value === 'Transfer Servisi' && item.badge) ||
      badge.some((item) => item.value === 'Skipass' && item.badge)
    );
  }, [badge]);
  const relevantPromotion = useMemo(() => {
    return itemPromotions?.find(
      (item) =>
        item.promotionType === HotelPromotionType.BANK_POS_PERCENT_DISCOUNT ||
        item.promotionType === HotelPromotionType.BANK_POS_VALUE_DISCOUNT
    );
  }, [itemPromotions]);

  const locationText = useMemo(() => {
    if (country) {
      if (countryCode) {
        return `${location}, ${country}, ${countryCode}`;
      } else {
        return `${location}, ${country}`;
      }
    } else {
      if (countryCode) {
        return `${location}, ${countryCode}`;
      } else {
        return location;
      }
    }
  }, [location, countryCode, country]);

  return (
    <MoileStyledListingItemWrapper initialItem={initialItem}>
      {hasEvents ? (
        <EventsRowDesktop>
          <EventsBadgeWrapper>
            <CustomIcon fontSize={16} icon={SmilingStar} />
            <Typography
              color='NL0'
              variant='caption2'
              weight='SemiBold'
              style={{ textAlign: 'right', width: '82px' }}
            >
              Etkinlikli Otel
            </Typography>
          </EventsBadgeWrapper>
        </EventsRowDesktop>
      ) : null}
      <MoileStyledListingItem
        isPeriodHotel={Boolean(periodType)}
        initialItem={initialItem}
      >
        {periodType ? (
          <PeriodTypeWrapper>
            <Typography weight='SemiBold' variant='caption2' color='NL0'>
              {periodTypeViewver(periodType)}
            </Typography>
          </PeriodTypeWrapper>
        ) : null}
        <MobileStyledListingImageWrapper>
          <ListingItemSlider
            badge={badge.some(
              (item) => item.value === 'Misafirler Öneriyor' && item.badge
            )}
            images={itemImages || []}
            secure={secure}
            suggests={suggests}
            fallBackImage={fallBackImage}
            seturExecutive={seturExecutive}
            href={`${url}`}
            name={name}
            initialItem={initialItem}
          />
        </MobileStyledListingImageWrapper>
        {ratings?.rating?.value || boardType ? (
          <MobileBoardTypeContainer>
            <BoardType boardTypes={boardTypes} boardType={boardType} />
            {ratings?.rating?.value &&
            ratings.provider === RatingProviderType.TRIP_ADVISOR ? (
              <ScoringBanner
                totalScoring={ratings?.rating?.value}
                reviews={ratings}
              />
            ) : null}
            {ratings?.rating?.value &&
            ratings.provider === RatingProviderType.EXPEDIA ? (
              <ExpediaScoringBanner
                totalScoring={ratings?.rating?.value}
                reviews={ratings}
              />
            ) : null}
          </MobileBoardTypeContainer>
        ) : null}

        <MobileLocationContainer>
          {distanceList?.[0]?.distance ? (
            <DistanceWrapper>
              <Typography weight='Regular' variant='caption2' color='NL300'>
                {`${distanceList[0].name}`}{' '}
              </Typography>
              <Typography weight='Regular' variant='caption2' color='NL300'>
                Merkez:{' '}
              </Typography>
              <Typography weight='SemiBold' color='NL800' variant='caption2'>
                {`${distanceList[0].distance
                  .toLocaleString('tr-TR', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 1,
                  })
                  ?.toString()
                  .replace(',', '.')} km`}
              </Typography>
            </DistanceWrapper>
          ) : null}
          <Typography
            color='NL300'
            weight='Regular'
            variant='caption3'
            style={{ maxWidth: '230px', lineHeight: '23px' }}
          >
            {locationText}
          </Typography>
        </MobileLocationContainer>

        <MobileNameContainer>
          <MobileNameWrapper>
            {expedia && (
              <ListItemStarContainer>{getStars}</ListItemStarContainer>
            )}
            <Link
              href={`${url}`}
              target='_blank'
              title={name ?? ''}
              onClick={() => onGoToHotelDetailClick?.()}
              prefetch={false}
            >
              <MobileHotelNameTextWrapper>
                <Typography color='NL800' variant='caption' weight='Bold'>
                  {name}
                </Typography>
              </MobileHotelNameTextWrapper>
            </Link>
          </MobileNameWrapper>
          <ShareWrapperMobile>
            <MyFavoriteComponent
              itemId={id}
              itemName={name}
              imageUrl={itemImages?.[0]?.imageUrl ?? ''}
              type={FavoriteItemType.HOTEL}
              isListing={true}
              itemUrl={url?.split('?')[0]}
              favoriteLists={favoriteLists}
              refetchFavoriteLists={refetchFavoriteLists}
              defaultList={defaultList}
              defaultListItems={defaultListItems}
              refetchDefaultList={refetchDefaultList}
              fontColor='D400'
            />
            {expedia ? null : (
              <Difference
                diffData={diffData}
                setDiffData={setDiffData}
                differenceItem={differenceItem}
                addDiffData={addDiffData}
                removeDiffData={removeDiffData}
                isListing={true}
                setIsRedirecting={setIsRedirecting}
              />
            )}
            <SocialShare
              hotelListing={hotelListing}
              itemId={id}
              itemName={name}
              link={link ?? ''}
            />
          </ShareWrapperMobile>
        </MobileNameContainer>
        <MobilePriceAndBadgeWrapper>
          <PaymentTypesMobileBadgeWrapper>
            {/*7142 taskında chipin kartlar listeleme sayfasından kaldırıldı. */}
            {/* <ListChippinCard /> */}

            <>
              {countryCode === 'TR' || cyprus ? (
                <ListWorldLogo label={installments} />
              ) : null}
            </>

            {relevantPromotion && (
              <BankPosCard
                description={relevantPromotion.description}
                promotionValueDetail={
                  relevantPromotion.promotionValueDetail || 'Varsayılan değer'
                }
              />
            )}
          </PaymentTypesMobileBadgeWrapper>
          {checkInDate && checkOutDate ? (
            <>
              {!hasDiscount && price ? (
                <MobilePricingContainer>
                  {price.original ? (
                    <MobileDiscountAndNightCountWrapper>
                      <NightsCount
                        nightsCount={getNightCount(checkInDate, checkOutDate)}
                        feeDetails={price?.details}
                      />
                    </MobileDiscountAndNightCountWrapper>
                  ) : null}
                  <PriceLabel
                    priceWithDiscount={price?.discounted}
                    currency={getCurrencySymbol(price.currency)}
                    priceInEUR={expedia ? price.details?.total : undefined}
                    originalPrice={originalPrice}
                    rate={price.rate}
                    bankPosPromotionPrice={price.bankPosPromotionPrice}
                    bankPosPromotionRate={price.bankPosPromotionRate}
                    basketHotelCount={basketHotelCount}
                  />
                </MobilePricingContainer>
              ) : (
                <MobilePricingContainer>
                  <MobileDiscountAndNightCountWrapper>
                    {price?.details ? (
                      <NightDiscountWrapperWithDetails>
                        <NightsCount
                          nightsCount={getNightCount(checkInDate, checkOutDate)}
                          feeDetails={price?.details}
                        />
                        {price && price.rate ? (
                          <DiscountLabel
                            discount={price.rate}
                            type={getDiscountType(price.type)}
                          />
                        ) : null}
                      </NightDiscountWrapperWithDetails>
                    ) : (
                      <NightDiscountWrapperWithoutDetails>
                        <NightsCount
                          nightsCount={getNightCount(checkInDate, checkOutDate)}
                          feeDetails={price?.details}
                        />
                        {price && price.rate ? (
                          <DiscountLabel
                            discount={price.rate}
                            type={getDiscountType(price.type)}
                          />
                        ) : null}
                      </NightDiscountWrapperWithoutDetails>
                    )}
                  </MobileDiscountAndNightCountWrapper>

                  {price ? (
                    <PriceLabel
                      total={price.details?.total !== price.original && expedia}
                      priceWithDiscount={price?.discounted}
                      priceInEUR={expedia ? price.details?.total : undefined}
                      currency={getCurrencySymbol(price.currency)}
                      originalPrice={originalPrice}
                      rate={price.rate}
                      bankPosPromotionPrice={price.bankPosPromotionPrice}
                      bankPosPromotionRate={price.bankPosPromotionRate}
                      basketHotelCount={basketHotelCount}
                    />
                  ) : null}
                </MobilePricingContainer>
              )}
            </>
          ) : null}
        </MobilePriceAndBadgeWrapper>
        {itemPromotions && itemPromotions.length ? (
          <Promotions itemPromotions={itemPromotions} />
        ) : null}
        {highlights && highlights.length ? (
          <Features hightlights={highlights} />
        ) : null}
        {showBadgeContainer ? (
          <MobileBadgeContainer>
            {badge.some(
              (item) => item.value === 'Misafirler Öneriyor' && item.badge
            ) ? (
              <Badge
                icon={InfoIcon}
                text='Misafirler Öneriyor'
                isGuestsSuggests={true}
              />
            ) : null}
            {badge.some(
              (item) =>
                item.value === 'Sürdürülebilir Turizm Sertifikası' && item.badge
            ) ? (
              <Badge
                icon={SustainabilityIconNext}
                text='Sürdürülebilir Turizm Sertifikalı'
              />
            ) : null}
            {badge.some((item) => item.value === 'Skipass' && item.badge) ? (
              <Badge icon={SkipassIconNext} text='Ücretsiz Skipass' />
            ) : null}
            {badge.some(
              (item) => item.value === 'Transfer Servisi' && item.badge
            ) ? (
              <Badge
                icon={AirportTransferNext}
                text='Ücretsiz Havalimanı Transferi'
              />
            ) : null}
          </MobileBadgeContainer>
        ) : null}

        {events?.length ? <HotelListingEvents events={events} /> : null}

        {!checkInDate || !checkOutDate ? (
          <MobileDateSelectionContainer>
            <MobileDateSelectionWarningMessage>
              <Typography variant='caption2' weight='Regular' color='BR500'>
                Fiyatı görmek için tarih seçiniz
              </Typography>
            </MobileDateSelectionWarningMessage>
            <MobileDateSelectionButtonContainer>
              <MobileDateSelectionButton onClick={handleDateSelection}>
                Tarih Seç
              </MobileDateSelectionButton>
              <ShowHotelButtonWithDateSelection
                onClick={() => onGoToHotelDetail?.()}
              >
                <Typography color='NL800' variant='caption2' weight='Bold'>
                  Oteli İncele
                </Typography>
                <CustomIcon
                  width={14}
                  height={14}
                  fill='NL800'
                  icon={ChevronRight}
                />
              </ShowHotelButtonWithDateSelection>
            </MobileDateSelectionButtonContainer>
          </MobileDateSelectionContainer>
        ) : (
          <MobileSelectHotelButton
            periodType={true}
            onClick={() => onGoToHotelDetail?.()}
          >
            Oteli Seç
          </MobileSelectHotelButton>
        )}
      </MoileStyledListingItem>
    </MoileStyledListingItemWrapper>
  );
};

export default MobileListingItem;
